import { Grid, Pagination } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Alert from '../../../components/Alert/Alert';
import { useAppSelector } from '../../../reduxService/hooks';
import { IProjectBaseResponse } from '../../api/IApiResponse';
import * as BeMaccaferriStoreHelper from '../../api/store/BeMaccaferriStoreHelper';
import { endpoint } from '../../pages';
import { selectUser } from '../../store/user/userSlice';
import { IRecentProjectsProps } from './IRecentProjects';
import ProjectItem from './ProjectItem/ProjectItem';
import './RecentProjects.scss';
import Button from '../../../components/Button/Button';
import Dialog from '../../../components/Dialog/Dialog';
import { cleanProjectInfo } from '../../store/projectInfo/projectInfoSlice';
import { useAppDispatch } from '../../../reduxService/hooks';
import { cleanProject as macro2CleanProject } from '../../Macro2/store/project/projectslice';
import { cleanProject as macro1CleanProject } from '../../Macro1/store/project/projectslice';

const MIN_PROJECT_PAGE_SIZE = 10;

const RecentProjects = (props: IRecentProjectsProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [projects, setProjects] = useState<IProjectBaseResponse[]>([]);
    const user = useAppSelector(selectUser);

    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [DeleteConfirmDialog, setDeleteConfirmDialog] = useState<boolean>(false);
    const [DeleteResultsDialog, setDeleteResultsDialog] = useState<string>('');
    const [ProjectToDel, setProjectToDel] = useState<IProjectBaseResponse | null>(null);

    const fetchProjects = async (page: number) => {
        let currentPage = page;
        let projectByCompany = await BeMaccaferriStoreHelper.getProjectsAsync(currentPage, MIN_PROJECT_PAGE_SIZE);
        while (projectByCompany.data.items.length == 0 && currentPage > 1) {
            currentPage--;
            projectByCompany = await BeMaccaferriStoreHelper.getProjectsAsync(currentPage, MIN_PROJECT_PAGE_SIZE);
        }
        setPage(currentPage);
        //console.log(projectByCompany)
        setProjects(projectByCompany.data.items);
        setTotalPages(projectByCompany.data.totalPages);
        setTotalItems(projectByCompany.data.totalCount);
    };

    const dispatch = useAppDispatch();
    const newProject = () => {
        dispatch(cleanProjectInfo());
        navigate(endpoint.projectInfo);
    }

    useEffect(() => {
        const fetchData = async () => {
            if (!!user.data?.email) {
                await fetchProjects(1);
            }
        };

        fetchData();
    }, [user.data]);

    const handleClick = async (e: any, project: IProjectBaseResponse) => {
        if (!!props.onClose) {
            props.onClose();
        }
        dispatch(macro2CleanProject());
        dispatch(macro1CleanProject());
        dispatch(cleanProjectInfo());
        navigate(`designCondition/${project.projectType}/${project.projectId}`);
    };

    const handleDelete = async (e: any, project: IProjectBaseResponse) => {
        setProjectToDel(project);
        setDeleteConfirmDialog(true);
        /*Alert.warning(t('recent_projects_alert_warning_title'), t('recent_projects_alert_warning_body') as string).then(async result => {
            if (result.isConfirmed) {
                let isDeleted = await BeMaccaferriStoreHelper.deleteProjectByIdAsync(project.projectId!);
                if (isDeleted) {
                    let newProjects = projects!.filter(proj => proj.projectId !== project.projectId);
                    setProjects(newProjects);
                    Alert.success(t('recent_projects_alert_success_title'), t('recent_projects_alert_success_body') as string);
                }
            }
        });*/
    };

    const confirmDelete = async () => {
        setDeleteConfirmDialog(false);
        if (!ProjectToDel) return;
        try {
            let isDeleted = await BeMaccaferriStoreHelper.deleteProjectByIdAsync(ProjectToDel.projectId!);
            if (isDeleted) {
                fetchProjects(page);
                /*let newProjects = projects!.filter(proj => proj.projectId !== ProjectToDel.projectId);
                setProjects(newProjects);*/
            }
            setDeleteResultsDialog(t('functions.projects.deleteText') as string);
        } catch (ex) {
            console.error(ex);
            setDeleteResultsDialog(t('functions.projects.deleteError') as string);
        }

    };

    const handlePageChange = (page: number) => {
        fetchProjects(page);
        setPage(page);
    };

    return (
        <>
            <Dialog
                open={DeleteConfirmDialog}
                title={`${t('warnings.generic.title')}`}
                onClose={() => setDeleteConfirmDialog(false)}
                actions={[
                    { text: `${t('warnings.generic.cancel')}`, callback: () => setDeleteConfirmDialog(false), color: 'secondary' }
                    , { text: `${t('warnings.generic.confirm')}`, callback: () => confirmDelete(), color: 'primary' }
                ]}
            >
                <>
                    <div>
                        {t('warnings.projects.deleteConfirm')}
                    </div>
                </>
            </Dialog>
            <Dialog
                open={!!DeleteResultsDialog && DeleteResultsDialog.length > 0}
                title={`${t('functions.projects.deleteTitle')}`}
                onClose={() => setDeleteResultsDialog('')}
            >
                <>
                    <div>
                        {DeleteResultsDialog}
                    </div>
                </>
            </Dialog>
            <Grid container style={{ paddingTop: '80px', alignItems: 'center', marginBottom: "40px" }} spacing={2}>
                <Grid item xs={8} style={{ paddingTop: '0px' }}>
                    <label className='tableTitle'>{t('home_recent_projects_table_title')}</label> <span className='counter'>{`[${totalItems}]`}</span>
                </Grid>
                <Grid item xs={4}>
                    <Button
                        className='d-flex justify-content-end'
                        classNameBtn='newProjectButton'
                        onClick={newProject}>
                        {t('home_new_project_button')}
                        <i className='fa-solid fa-plus-circle' />
                    </Button>

                </Grid>
            </Grid>

            {projects.length > 0 && (
                <>
                    <div className='d-flex p-1 me-3'>
                        <label className='w-30 me-5 tableHeader'>{t('home_recent_projects_table_name')}</label>
                        <label className='w-30 me-5 tableHeader'>{t('home_recent_projects_table_last_update')}</label>
                        <label className='w-30 tableHeader'>{t('home_recent_projects_table_type')}</label>
                    </div>
                    <hr />
                    {projects.map((project: IProjectBaseResponse, index: number) => (
                        <ProjectItem project={project} onClick={e => handleClick(e, project)} onDelete={e => handleDelete(e, project)} key={index} />
                    ))}
                    {page <= totalPages && totalPages !== 1 && (
                        <Pagination count={totalPages} page={page} onChange={(e, value) => handlePageChange(value)} className='d-flex justify-content-end mt-3' />
                    )}
                </>
            )}

        </>
    );
};

export default RecentProjects;
